import { ActionType, CompleteResourcesStepPayload } from "../../../../AppDataProvider/actions";
import { AppContext, BookingData } from "../../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, LoadingPill as BaseLoadingPill, List, ListItem } from "../../../../common/components";
import { corners, marginBottom, marginRight, marginTop, media, typography } from "../../../../common/styles";
import { CreateReservationMutationVariables, ResourceAllocationType, ResourceSelectionMode, useCreateReservationMutation } from "../../../../GeneratedGraphQL";
import { getReservationPartsInput, requireBookingDataFields } from "../../../../utils";
import React, { useContext, useState } from "react";
import { Resource, TimeSlot } from "../../../../AppDataProvider/types";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { PartRequired } from "../../../../utils/types";
import { sanitize } from "dompurify";
import styled from "styled-components";
import { useDeleteExistingReservedSpace } from "../../../Availability/hooks/useDeleteExistingReservedSpace";
import { useTranslation } from "react-i18next";
export type ResourceListProps = {
  isSubList?: boolean;
  resources: Resource[];
};
type ResourceStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "service" | "timezone">;
export function ResourceList(props: ResourceListProps): React.ReactElement {
  const {
    resources,
    isSubList
  } = props;
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  const {
    t
  } = useTranslation();
  const [createReservationLoading, setCreateReservationLoading] = useState<boolean>(false);
  const [deleteReservationLoading, setDeleteReservationLoading] = useState<boolean>(false);
  const [createReservation] = useCreateReservationMutation({
    errorPolicy: "all"
  });
  requireBookingDataFields<ResourceStepBookingData>(appData.bookingData, ["createdSource", "organisation", "service", "timezone"]);
  const {
    bookingTool
  } = appData;
  const {
    resourceSelectionMode
  } = bookingTool;
  const {
    bookingData
  } = appData;
  const {
    attendees,
    createdSource,
    duration,
    organisation,
    service,
    timezone,
    reservedSpaceId
  } = bookingData;
  const {
    allowResourceImageDisplay,
    resourceSelectionBeforeAvailability
  } = organisation;
  const {
    parts
  } = service;
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });
  type CreateStandardReservationOpts = {
    selectedResource: Resource;
    selectedTimeSlot: TimeSlot;
  };
  const createStandardReservation = async ({
    selectedResource,
    selectedTimeSlot
  }: CreateStandardReservationOpts) => {
    const {
      interval
    } = selectedTimeSlot;

    /**
     * Add 1 minute to reservation expiry on the back end.
     * This buffer means front-end will prompt for a reload 1 minute before
     * actual expiry, avoiding situations where user is interacting at same
     * time as reservation expiry.
     * Chronos only accepts full-minute values so 1 minute is easiest compromise
     */
    const {
      reservationExpiryMinutes
    } = organisation;
    const expiresAfter = reservationExpiryMinutes + 1;
    const variables: CreateReservationMutationVariables = {
      expiresAfter,
      parts: getReservationPartsInput({
        parts,
        selectedInterval: interval
      }),
      requiredSpaces: attendees,
      resourceId: selectedResource._id,
      serviceId: service._id,
      source: createdSource,
      timeZone: timezone
    };
    const {
      data,
      errors
    } = await createReservation({
      variables
    });
    if (errors?.some(error => error.message === "Response code 422 (Unprocessable Entity)")) {
      appDataDispatch({
        payload: {
          buttonLabel: t("reload"),
          content: t("errors.somethingChanged"),
          icon: faExclamationTriangle,
          onClick: /* istanbul ignore next */() => location.reload(),
          title: t("somethingChanged")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      return;
    } else if (!data || errors) {
      appDataDispatch({
        payload: {
          message: t("errors.cannotCreateReservation"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
      setCreateReservationLoading(false);
      return;
    }
    const {
      reservedSpace,
      reservation
    } = data.createReservation;
    setCreateReservationLoading(false);
    const updatedData = {
      attendees,
      duration,
      reservedSpaceId: reservedSpace._id,
      resource: selectedResource,
      startDate: new Date(reservation.start),
      timezone
    };
    const payload: CompleteResourcesStepPayload = {
      updatedData
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  };
  const selectResource = (options: {
    selectedResource: Resource;
  }) => {
    /**
     * We have come to this step after availability and now
     * we need to create the reservation using the selected
     * resource.
     */
    if (resourceSelectionMode !== ResourceSelectionMode.Disabled && service.resourceAllocationType === ResourceAllocationType.Any && bookingData.selectedTimeSlot && !resourceSelectionBeforeAvailability) {
      const handleReservations = async (selectedTimeSlot: TimeSlot) => {
        if (reservedSpaceId) {
          setDeleteReservationLoading(true);
          await deleteExistingReservedSpace();
          setDeleteReservationLoading(false);
        }
        setCreateReservationLoading(true);
        void createStandardReservation({
          selectedResource: options.selectedResource,
          selectedTimeSlot: selectedTimeSlot
        });
      };
      void handleReservations(bookingData.selectedTimeSlot);
      return;
    }
    const payload: CompleteResourcesStepPayload = {
      updatedData: {
        resource: options.selectedResource
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  };
  return <List data-sentry-element="List" data-sentry-component="ResourceList" data-sentry-source-file="ResourceList.tsx">
      {(createReservationLoading || deleteReservationLoading) && <LoadingPill />}

      {resources.map(resource => {
      return <ListItem key={resource._id}>
            <Content>
              <TitleRow>
                {allowResourceImageDisplay && resource.profilePhotoUrl && <Image src={resource.profilePhotoUrl} alt={resource.name} />}

                <Name as={isSubList ? "h3" : "h2"}>{resource.name}</Name>
              </TitleRow>
              {resource.description && <Description dangerouslySetInnerHTML={{
            __html: sanitize(resource.description)
          }} />}
            </Content>
            <Button onClick={() => {
          selectResource({
            selectedResource: resource
          });
        }}>
              {t("select")}
            </Button>
          </ListItem>;
    })}
    </List>;
}
const Content = styled.div`
  display: flex;
  flex-direction: column;
`;
const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${marginBottom.s}
  &:last-child {
    @media ${media.tablet}, ${media.desktop} {
      ${marginBottom.zero}
    }
  }
`;
const Image = styled.img`
  height: 3rem;
  width: 3rem;
  object-fit: cover;
  ${corners.circle}
  ${marginRight.m}
`;
const Name = styled.h2`
  ${typography.subtitle}
  ${marginTop.zero}
`;
const Description = styled.div`
  ${typography.body}
  ${marginBottom.zero}
  @media ${media.mobile} {
    ${marginBottom.s}
  }
`;
const Button = styled(BaseButton)`
  min-width: 6.25rem;
`;
const LoadingPill = styled(BaseLoadingPill)`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: absolute;
  justify-self: center;
  align-self: center;
`;
export default ResourceList;